import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  Platform,
  Dimensions,
  Switch,
} from 'react-native';
import { overlayBackdropStyle, overlayCardLook } from './OverlayPanel';
import { InsetModal as Modal } from './InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { PhoneInputComponent } from './design-system/PhoneInput';
import { Input } from './design-system/Input';
import { Button } from './design-system/Button';
import { useAppStore } from '../store/appStore';
import { useAppContext } from '../contexts/AppContext';
import { apiService } from '../services/api';
import { parseInternationalPhoneNumber, getCountryFromPhone } from '../utils/phoneNumber';
import { usePermissions } from '../hooks/usePermissions';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { defaultPhoneCountry } from '../utils/businessDefaults';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
const isMobile = screenWidth < 768;

interface WhatsAppContactModalProps {
  visible: boolean;
  onClose: () => void;
  onContactAdded?: (contact: { name: string; phoneNumber: string }) => void;
  onConversationStarted?: (contact: { name: string; phoneNumber: string; topic: string }) => void;
  initialPhoneNumber?: string;      // Pre-fill phone number
  initialContactName?: string;       // Pre-fill contact name
  autoStartConversation?: boolean;   // Auto-enable conversation toggle
}

export const WhatsAppContactModal: React.FC<WhatsAppContactModalProps> = ({
  visible,
  onClose,
  onContactAdded,
  onConversationStarted,
  initialPhoneNumber = '',
  initialContactName = '',
  autoStartConversation = false,
}) => {
  const styles = useThemedStyles(getStyles);
  const { user, currentBusiness, currentSubscription } = useAppStore();
  const { navigateTo, showNotification } = useAppContext();
  const { hasPermissionAtLeast } = usePermissions();
  const canManageContacts = hasPermissionAtLeast('contacts.manage', 'edit');

  const [contactName, setContactName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [topic, setTopic] = useState('');
  const [startConversation, setStartConversation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingContact, setIsCheckingContact] = useState(false);
  const [existingContact, setExistingContact] = useState<any>(null);
  const [showContactNameField, setShowContactNameField] = useState(false);
  const [messageLimits, setMessageLimits] = useState<{ canSend: boolean; message: string; current: number; max: number } | null>(null);
  const [isLoadingLimits, setIsLoadingLimits] = useState(false);
  const [initialCountryCode, setInitialCountryCode] = useState<string>(() => defaultPhoneCountry());
  const phoneInputRef = React.useRef<any>(null);

  // Load message limits when modal opens
  const loadMessageLimits = async () => {
    try {
      setIsLoadingLimits(true);
      const response = await apiService.checkWhatsAppLimits();
      if (response.statusCode === 1000 && response.data) {
        setMessageLimits(response.data);
      }
    } catch (error) {
      console.error('Error loading message limits:', error);
      // Set default limits if API fails
      setMessageLimits({ canSend: true, message: 'Unable to load limits', current: 0, max: 500 });
    } finally {
      setIsLoadingLimits(false);
    }
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    console.log('📞 [WhatsAppContactModal] Modal visibility changed:', {
      visible,
      initialPhoneNumber,
      initialContactName,
      autoStartConversation
    });
    
    if (visible) {
      setContactName(initialContactName);
      
      // Parse initial phone number to extract national number and country
      if (initialPhoneNumber) {
        console.log('📞 [WhatsAppContactModal] Parsing initial phone number:', initialPhoneNumber);
        const parsed = parseInternationalPhoneNumber(initialPhoneNumber);
        
        if (parsed && parsed.isValid) {
          console.log('📞 [WhatsAppContactModal] Parsed successfully:', {
            country: parsed.country,
            countryCode: parsed.countryCode,
            nationalNumber: parsed.nationalNumber,
            international: parsed.internationalFormat
          });
          
          // Set country code first (this will be used by PhoneInput initialCountry)
          if (parsed.country) {
            console.log('📞 [WhatsAppContactModal] Setting initial country code to:', parsed.country);
            setInitialCountryCode(parsed.country);
          }
          
          // Set only the national number (without country code)
          console.log('📞 [WhatsAppContactModal] Setting phone number to:', parsed.nationalNumber);
          setPhoneNumber(parsed.nationalNumber);
        } else {
          console.log('📞 [WhatsAppContactModal] Could not parse, using as-is:', initialPhoneNumber);
          // Fallback: try to extract national number manually
          const cleanPhone = initialPhoneNumber.replace(/^\+/, '');
          console.log('📞 [WhatsAppContactModal] Cleaned phone:', cleanPhone);
          setPhoneNumber(cleanPhone);
          setInitialCountryCode(defaultPhoneCountry()); // Default to Egypt
        }
      } else {
        console.log('📞 [WhatsAppContactModal] No initial phone number provided');
        setPhoneNumber('');
        setInitialCountryCode(defaultPhoneCountry());
      }
      
      setTopic('');
      setStartConversation(false); // Will be set after limits load
      setExistingContact(null);
      setShowContactNameField(!!initialContactName || !!initialPhoneNumber);
      loadMessageLimits();
    }
  }, [visible, initialPhoneNumber, initialContactName, autoStartConversation]);

  // Auto-enable conversation toggle when limits are loaded and autoStartConversation is true
  useEffect(() => {
    if (visible && autoStartConversation && messageLimits) {
      setStartConversation(messageLimits.canSend);
    }
  }, [visible, autoStartConversation, messageLimits]);

  // Check if contact exists when phone number changes
  useEffect(() => {
    const checkExistingContact = async () => {
      if (!phoneNumber.trim() || phoneNumber.length < 8) {
        setExistingContact(null);
        setShowContactNameField(false);
        return;
      }

      try {
        setIsCheckingContact(true);
        
        // Get formatted phone number
        let formattedPhoneNumber = phoneNumber;
        if (phoneInputRef.current) {
          const phoneData = phoneInputRef.current.getValue();
          if (phoneData?.formattedNumber) {
            formattedPhoneNumber = phoneData.formattedNumber;
          } else if (phoneData?.dialCode && !phoneNumber.startsWith('+')) {
            formattedPhoneNumber = `${phoneData.dialCode}${phoneNumber}`;
          }
        }

        // Ensure phone number starts with +
        if (!formattedPhoneNumber.startsWith('+')) {
          if (phoneInputRef.current) {
            const phoneData = phoneInputRef.current.getValue();
            if (phoneData?.dialCode) {
              formattedPhoneNumber = `${phoneData.dialCode}${phoneNumber}`;
            } else {
              formattedPhoneNumber = `+${phoneNumber}`;
            }
          } else {
            formattedPhoneNumber = `+${phoneNumber}`;
          }
        }

        // Check if contact exists
        const response = await apiService.checkContactExists(formattedPhoneNumber);
        
        if (response.statusCode === 1000 && response.data?.exists) {
          setExistingContact(response.data.contact);
          setContactName(response.data.contact.name || '');
          setShowContactNameField(true); // Show field with existing name
        } else {
          setExistingContact(null);
          setContactName('');
          setShowContactNameField(true); // Show field for new contact
        }
      } catch (error) {
        console.error('Error checking existing contact:', error);
        setExistingContact(null);
        setContactName('');
        setShowContactNameField(true); // Show field even on error
      } finally {
        setIsCheckingContact(false);
      }
    };

    const timeoutId = setTimeout(checkExistingContact, 500); // Debounce
    return () => clearTimeout(timeoutId);
  }, [phoneNumber]);

  const handleSave = async () => {
    if (!canManageContacts) return;
    if (!phoneNumber.trim()) {
      Alert.alert('Missing Information', 'Please enter a phone number.');
      return;
    }

    if (!showContactNameField || !contactName.trim()) {
      Alert.alert('Missing Information', 'Please wait for contact verification or enter a valid phone number.');
      return;
    }

    if (startConversation && !topic.trim()) {
      Alert.alert('Missing Information', 'Please enter a topic for the conversation.');
      return;
    }

    if (!currentBusiness || !currentSubscription) {
      Alert.alert('Configuration Error', 'No business or subscription found.');
      return;
    }

    try {
      setIsLoading(true);
      
      // Get formatted phone number
      let formattedPhoneNumber = phoneNumber;
      if (phoneInputRef.current) {
        const phoneData = phoneInputRef.current.getValue();
        if (phoneData?.formattedNumber) {
          formattedPhoneNumber = phoneData.formattedNumber;
        } else if (phoneData?.dialCode && !phoneNumber.startsWith('+')) {
          formattedPhoneNumber = `${phoneData.dialCode}${phoneNumber}`;
        }
      }

      // Ensure phone number starts with +
      if (!formattedPhoneNumber.startsWith('+')) {
        if (phoneInputRef.current) {
          const phoneData = phoneInputRef.current.getValue();
          if (phoneData?.dialCode) {
            formattedPhoneNumber = `${phoneData.dialCode}${phoneNumber}`;
          } else {
            formattedPhoneNumber = `+${phoneNumber}`;
          }
        } else {
          formattedPhoneNumber = `+${phoneNumber}`;
        }
      }

      // Validate phone number - support different country formats
      if (formattedPhoneNumber.length < 8) {
        Alert.alert('Invalid Phone Number', 'Please enter a valid phone number.');
        return;
      }

      if (startConversation) {
        // Start conversation with template message
        await startWhatsAppConversation(formattedPhoneNumber, contactName.trim(), topic.trim());
      } else {
        // Just add contact to database
        await addContactToDatabase(formattedPhoneNumber, contactName.trim());
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      const errorMessage = (error as Error).message || 'Failed to save contact. Please try again.';
      
      // Handle specific WhatsApp API errors
      if (errorMessage.includes('Account not registered') || errorMessage.includes('133010')) {
        Alert.alert(
          'WhatsApp Account Issue',
          'Your WhatsApp Business account is not registered with Meta. Please:\n\n1. Verify your phone number with Meta\n2. Complete your WhatsApp Business setup\n3. Ensure your account is approved\n\nContact support if you need help with the setup process.',
          [{ text: 'OK' }]
        );
      } else if (errorMessage.includes('Invalid phone number')) {
        Alert.alert('Invalid Phone Number', 'Please check the phone number format and try again.');
      } else if (errorMessage.includes('Template')) {
        Alert.alert('Template Error', 'There was an issue with the message template. Please try again or contact support.');
      } else {
        Alert.alert('Error', errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const addContactToDatabase = async (formattedPhoneNumber: string, contactName: string) => {
    try {
      // If contact already exists, don't update the name
      if (existingContact) {
        showNotification('info', 'Contact Exists', `Contact with this number already exists: ${existingContact.name}`);
        if (onContactAdded) {
          onContactAdded({ name: existingContact.name, phoneNumber: formattedPhoneNumber });
        }
        handleClose();
        return;
      }

      // Check if parent contact already exists
      console.log('[WhatsAppContactModal] Checking for existing parent contact with phone:', formattedPhoneNumber);
      try {
        const searchResponse = await apiService.searchParentContactsByIdentifiers({
          phone: formattedPhoneNumber,
        });
        
        console.log('[WhatsAppContactModal] Search response:', searchResponse);
        
        if (searchResponse.statusCode === 1000 && searchResponse.data && Array.isArray(searchResponse.data) && searchResponse.data.length > 0) {
          // Parent contact already exists - update it if needed
          const existingParent = searchResponse.data[0];
          if (contactName.trim() && contactName.trim() !== existingParent.profile?.name) {
            await apiService.updateParentContact(existingParent._id || existingParent.id, {
              profile: {
                name: contactName.trim(),
              },
            });
          }
          showNotification('success', 'Contact Added', `${contactName || existingParent.profile?.name || formattedPhoneNumber} has been added to your contacts`);
          if (onContactAdded) {
            onContactAdded({ name: contactName || existingParent.profile?.name || formattedPhoneNumber, phoneNumber: formattedPhoneNumber });
          }
          handleClose();
          return;
        }
      } catch (error) {
        console.error('Error checking for existing parent contact:', error);
      }
      
      // Create new parent contact
      // Note: We don't include channels array for manual creation since we don't have channelContactId yet
      // The channel will be linked automatically when the contact interacts through WhatsApp
      const contactData = {
        primaryIdentifier: formattedPhoneNumber,
        primaryIdentifierType: 'phone',
        identifiers: {
          phone: [formattedPhoneNumber], // Backend expects arrays
          whatsapp: [formattedPhoneNumber], // Backend expects arrays
        },
        profile: {
          name: contactName.trim() || formattedPhoneNumber,
          phone: formattedPhoneNumber,
        },
        // Don't include channels - channelContactId is required but we don't have it for manual creation
        // The channel will be linked when the contact actually interacts through WhatsApp
      };
      console.log('[WhatsAppContactModal] Creating parent contact with data:', contactData);
      
      try {
        const createResponse = await apiService.createParentContact(contactData);
        
        console.log('[WhatsAppContactModal] Create parent contact response:', createResponse);
        
        if (createResponse.statusCode === 1000 && createResponse.data) {
          console.log('[WhatsAppContactModal] ✅ Parent contact created successfully:', createResponse.data);
          showNotification('success', 'Contact Added', `${contactName || formattedPhoneNumber} has been added to your contacts`);
          if (onContactAdded) {
            onContactAdded({ name: contactName || formattedPhoneNumber, phoneNumber: formattedPhoneNumber });
          }
          handleClose();
        } else {
          const errorMsg = createResponse.errors?.[0]?.message || createResponse.message || 'Failed to create parent contact';
          console.error('[WhatsAppContactModal] ❌ Failed to create parent contact:', errorMsg, createResponse);
          throw new Error(errorMsg);
        }
      } catch (error: any) {
        console.error('[WhatsAppContactModal] ❌ Error creating parent contact:', error);
        console.error('[WhatsAppContactModal] Error details:', {
          message: error.message,
          response: error.response,
          statusCode: error.statusCode,
          errors: error.errors,
        });
        
        // Show error to user instead of silently falling back
        const errorMessage = error.message || error.response?.data?.message || 'Failed to create parent contact. Please check the console for details.';
        console.error('[WhatsAppContactModal] Full error object:', JSON.stringify(error, null, 2));
        showNotification('error', 'Error', errorMessage);
        
        // Don't fallback to old API - we want to use parent contacts
        // Re-throw to be caught by outer catch
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error adding contact to database:', error);
      throw error;
    }
  };

  const startWhatsAppConversation = async (formattedPhoneNumber: string, contactName: string, topic: string) => {
    try {
      // Check message limits before proceeding
      const limitsResponse = await apiService.checkWhatsAppLimits();
      if (limitsResponse.statusCode === 1000 && limitsResponse.data) {
        const { canSend, message, current, max } = limitsResponse.data;
        if (!canSend) {
          Alert.alert(
            'Message Limit Reached', 
            `${message}\n\nCurrent: ${current}/${max} billable messages`,
            [{ text: 'OK' }]
          );
          return;
        }
      }

      // Use existing contact name if contact already exists
      const finalContactName = existingContact ? existingContact.name : contactName;
      
      // Get WhatsApp credentials
      const wabuService = currentSubscription?.services?.wabu as any;
      const whatsappCredentials = wabuService?.metadata?.whatsapp_credentials || {};
      
      if (!wabuService?.metadata?.whatsapp_phone?.length) {
        Alert.alert('Configuration Error', 'No WhatsApp phone number configured for this business.');
        return;
      }

      const phone = wabuService.metadata.whatsapp_phone[0];
      const phoneId = phone.whatsapp_phone_number_id || phone.phone_id || phone.id || phone.phone_number_id;

      if (!phoneId) {
        Alert.alert('Configuration Error', 'Invalid WhatsApp phone number configuration.');
        return;
      }

      // Template parameters for conversation
      const templateParameters = [
        currentBusiness?.name || 'DISLIX', // Header: business_name
        topic.replace(/[\n\r\t]/g, ' ').replace(/\s{2,}/g, ' ').trim() // Body: user's custom message (sanitized)
      ];

      // Send template message
      const response = await apiService.sendWhatsAppTemplateMessage({
        to: formattedPhoneNumber,
        templateName: 'discuss_ar',
        businessPhoneNumberId: phoneId,
        parameters: templateParameters,
        reason: topic,
        accessToken: whatsappCredentials.access_token,
        businessAccountId: whatsappCredentials.business_account_id,
        businessId: currentBusiness?._id,
        contactName: finalContactName, // Use the final contact name (existing or new)
        userId: user?.id,
        contactId: existingContact?.id,
      });

      if (response.statusCode === 1000) {
        const userName = user?.firstName + ' ' + user?.lastName || 'User';
        showNotification('success', 'Conversation Started', `WhatsApp conversation with ${finalContactName} has been initiated by ${userName}`);
        if (onConversationStarted) {
          onConversationStarted({ name: userName, phoneNumber: formattedPhoneNumber, topic });
        }
        handleClose();
      } else {
        throw new Error(response.errors?.[0]?.message || 'Failed to start conversation');
      }
    } catch (error) {
      console.error('Error starting WhatsApp conversation:', error);
      const errorMessage = (error as Error).message || 'Failed to start conversation';
      
      // Handle specific WhatsApp API errors
      if (errorMessage.includes('Account not registered') || errorMessage.includes('133010')) {
        Alert.alert(
          'WhatsApp Account Issue',
          'Your WhatsApp Business account is not registered with Meta. Please:\n\n1. Verify your phone number with Meta\n2. Complete your WhatsApp Business setup\n3. Ensure your account is approved\n\nContact support if you need help with the setup process.',
          [{ text: 'OK' }]
        );
      } else if (errorMessage.includes('Invalid phone number')) {
        Alert.alert('Invalid Phone Number', 'Please check the phone number format and try again.');
      } else if (errorMessage.includes('Template')) {
        Alert.alert('Template Error', 'There was an issue with the message template. Please try again or contact support.');
      } else if (errorMessage.includes('Rate limit')) {
        Alert.alert('Rate Limit', 'You have exceeded the message rate limit. Please wait before sending more messages.');
      } else {
        Alert.alert('Error', errorMessage);
      }
      
      throw error;
    }
  };

  const handleClose = () => {
    setContactName('');
    setPhoneNumber('');
    setTopic('');
    setStartConversation(false);
    setExistingContact(null);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <View style={styles.whatsappIcon}>
                <Ionicons name="logo-whatsapp" size={24} color="white" />
              </View>
              <Text style={styles.title}>WhatsApp Contact</Text>
            </View>
            <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={colors.text.secondary} />
            </TouchableOpacity>
          </View>

          {/* Content */}
          <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
            <View style={styles.businessInfo}>
              <Text style={styles.businessName}>{currentBusiness?.name}</Text>
              <Text style={styles.businessSubtext}>
                {startConversation ? 'Add contact and start conversation' : 'Add contact to database'}
              </Text>
            </View>

            <View style={styles.formContainer}>
              {/* Message Limits Progress Bar */}
              {messageLimits && (
                <View style={styles.limitsContainer}>
                  <View style={styles.limitsHeader}>
                    <Text style={styles.limitsTitle}>Customer Sessions</Text>
                    {isLoadingLimits ? (
                      <ActivityIndicator size="small" color={colors.primary} />
                    ) : (
                      <Text style={styles.limitsCount}>
                        {messageLimits.current}/{messageLimits.max}
                      </Text>
                    )}
                  </View>
                  <View style={styles.progressBarContainer}>
                    <View 
                      style={[
                        styles.progressBar, 
                        { 
                          width: `${Math.min((messageLimits.current / messageLimits.max) * 100, 100)}%`,
                          backgroundColor: messageLimits.current >= messageLimits.max ? colors.error : colors.primary
                        }
                      ]} 
                    />
                  </View>
                  <Text style={[
                    styles.limitsMessage,
                    { color: messageLimits.current >= messageLimits.max ? colors.error : colors.text.secondary }
                  ]}>
                    {messageLimits.message}
                  </Text>
                </View>
              )}

              {/* Phone Number - First field */}
              <View style={styles.phoneInputSection}>
                <Text style={styles.phoneLabel}>Phone Number *</Text>
                {(() => {
                  console.log('📞 [WhatsAppContactModal] Rendering PhoneInput with:', {
                    phoneNumber,
                    initialCountryCode,
                    key: `phone-input-${initialCountryCode}`
                  });
                  return null;
                })()}
                <PhoneInputComponent
                  key={`phone-input-${initialCountryCode}-${visible}`}
                  ref={phoneInputRef}
                  value={phoneNumber}
                  onChangeText={setPhoneNumber}
                  onChange={setPhoneNumber}
                  placeholder="Enter phone number"
                  style={styles.phoneInput}
                  initialCountry={initialCountryCode}
                />
                {!phoneNumber.trim() && isLoading && (
                  <Text style={styles.errorText}>Phone number is required</Text>
                )}
                
                {/* Existing Contact Warning */}
                {existingContact && (
                  <View style={styles.existingContactWarning}>
                    <Ionicons name="checkmark-circle" size={16} color={colors.success} />
                    <Text style={styles.existingContactText}>
                      Contact found: {existingContact.name}. Using existing contact data.
                    </Text>
                  </View>
                )}
                
                {/* Checking Contact Indicator */}
                {isCheckingContact && (
                  <View style={styles.checkingContact}>
                    <ActivityIndicator size="small" color={colors.primary} />
                    <Text style={styles.checkingContactText}>Checking existing contacts...</Text>
                  </View>
                )}
              </View>

              {/* Contact Name - Only show when phone number is entered */}
              {showContactNameField && (
                <Input
                  label={existingContact ? "Contact Name (Existing)" : "Contact Name *"}
                  value={contactName}
                  onChangeText={existingContact ? undefined : setContactName}
                  placeholder={existingContact ? "Contact name from database" : "Enter contact name"}
                  autoCapitalize="words"
                  editable={!existingContact}
                  inputStyle={existingContact ? styles.disabledInput : undefined}
                  error={!contactName.trim() && isLoading ? 'Contact name is required' : undefined}
                />
              )}

              {/* Start Conversation Toggle */}
              <View style={styles.toggleSection}>
                <View style={styles.toggleLabel}>
                  <Text style={styles.toggleTitle}>Start Conversation</Text>
                  <Text style={styles.toggleSubtitle}>
                    Send a template message to open 24-hour window
                  </Text>
                  {messageLimits && !messageLimits.canSend && (
                    <Text style={styles.limitReachedText}>
                      ⚠️ Customer session limit reached - Cannot start new conversations
                    </Text>
                  )}
                </View>
                <Switch
                  value={startConversation}
                  onValueChange={setStartConversation}
                  trackColor={{ 
                    false: messageLimits ? (!messageLimits.canSend ? colors.borderLight : colors.border) : colors.border, 
                    true: colors.primary 
                  }}
                  thumbColor={startConversation ? colors.white : colors.text.secondary}
                  disabled={messageLimits ? !messageLimits.canSend : false}
                />
              </View>

              {/* Topic Field - Only shown when start conversation is enabled */}
              {startConversation && (
                <Input
                  label="Conversation Topic *"
                  value={topic}
                  onChangeText={setTopic}
                  placeholder="Enter topic or reason for conversation"
                  multiline
                  numberOfLines={3}
                  error={!topic.trim() && isLoading ? 'Topic is required' : undefined}
                />
              )}
            </View>
          </ScrollView>

          {/* Footer */}
          <View style={styles.footer}>
            <Button
              variant="outline"
              onPress={handleClose}
              disabled={isLoading}
              style={styles.cancelButton}
              title="Cancel"
            />
            <Button
              variant="primary"
              onPress={handleSave}
              disabled={
                isLoading || 
                !showContactNameField || 
                !contactName.trim() || 
                !phoneNumber.trim() || 
                (startConversation && !topic.trim()) ||
                (messageLimits ? !messageLimits.canSend && startConversation : false)
              }
              loading={isLoading}
              style={styles.saveButton}
              title={startConversation ? 'Start Conversation' : 'Add Contact'}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: {
    flex: 1,
    ...overlayBackdropStyle(),
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
  },
  modalContainer: {
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: spacing.borderRadius.lg,
    width: isMobile ? '95%' : '85%',
    maxWidth: isMobile ? screenWidth - (spacing.lg * 2) : 600,
    maxHeight: '85%',
    elevation: 8,
    shadowColor: colors.text.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    ...overlayCardLook(),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  whatsappIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#25D366',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
    flex: 1,
  },
  closeButton: {
    padding: spacing.sm,
    marginLeft: spacing.sm,
  },
  content: {
    padding: spacing.lg,
  },
  businessInfo: {
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.lg,
    borderWidth: 1,
    borderColor: colors.border,
  },
  businessName: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  businessSubtext: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.primary,
  },
  formContainer: {
    gap: spacing.lg,
  },
  phoneInputSection: {
    marginBottom: spacing.sm,
  },
  phoneLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },
  phoneInput: {
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  errorText: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
    marginTop: spacing.xs,
    fontFamily: typography.fontFamily.primary,
  },
  existingContactWarning: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#D4EDDA',
    padding: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
    marginTop: spacing.sm,
    borderWidth: 1,
    borderColor: '#C3E6CB',
  },
  existingContactText: {
    fontSize: typography.fontSize.sm,
    color: colors.success,
    marginLeft: spacing.xs,
    fontFamily: typography.fontFamily.primary,
    flex: 1,
  },
  checkingContact: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.sm,
  },
  checkingContactText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginLeft: spacing.xs,
    fontFamily: typography.fontFamily.primary,
  },
  toggleSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  toggleLabel: {
    flex: 1,
    marginRight: spacing.md,
  },
  toggleTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  toggleSubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.primary,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    gap: spacing.md,
  },
  cancelButton: {
    flex: 1,
  },
  saveButton: {
    flex: 1,
    backgroundColor: '#25D366',
  },
  buttonIcon: {
    marginRight: spacing.xs,
  },
  disabledInput: {
    backgroundColor: colors.background.secondary,
    opacity: 0.7,
  },
  limitsContainer: {
    backgroundColor: colors.background.secondary,
    borderRadius: 8,
    padding: spacing.md,
    marginBottom: spacing.lg,
  },
  limitsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  limitsTitle: {
    fontSize: 14,
    fontFamily: typography.fontFamily.primary,
    fontWeight: '600',
    color: colors.text.primary,
  },
  limitsCount: {
    fontSize: 14,
    fontFamily: typography.fontFamily.primary,
    fontWeight: '600',
    color: colors.text.primary,
  },
  progressBarContainer: {
    height: 6,
    backgroundColor: colors.border,
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: spacing.sm,
  },
  progressBar: {
    height: '100%',
    borderRadius: 3,
  },
  limitsMessage: {
    fontSize: 12,
    fontFamily: typography.fontFamily.primary,
    color: colors.text.secondary,
  },
  limitReachedText: {
    fontSize: 12,
    fontFamily: typography.fontFamily.primary,
    color: colors.error,
    marginTop: spacing.xs,
    fontWeight: '500',
  },
});
